import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import rrulePlugin from '@fullcalendar/rrule'
import nlLocale from '@fullcalendar/core/locales/nl';
import Modal from '../common/Modal.jsx';
import DeleteModal from '../common/DeleteModal.jsx';
import { useState, useEffect, useMemo, useRef, useContext } from 'react';
import { client } from '../../hooks/Client.js';
import firebase from '../../firebase/compat'
import TaskEditor from '../tasks/TaskEditor.jsx';
import { useTaskDTO } from '../../hooks/firebase/TaskDto.jsx';
import useSettings from '../../hooks/Settings.jsx';
import resolveAgendaOptions, { buildToolbarRight, pickInitialView, HOUSEHOLD_PLANNING_BUTTON } from '../../helpers/calendar/resolveAgendaOptions.js';
import { getTaskTypeStyle } from '../../helpers/taskTypeStyles';
import { getAdressLabels } from '../../helpers/getAdressDescription';
import isTaskOutsidePersonaSchedule from '../../helpers/calendar/isTaskOutsidePersonaSchedule';
import personaAbsenceClassNames from '../../helpers/calendar/personaAbsenceClassNames';
import { agendaEventStyle } from '../../helpers/calendar/personaAgendaColors';
import splitScheduleAroundVisits from '../../helpers/calendar/splitScheduleAroundVisits';
import afterHouseVisitConfirmed from '../../helpers/adressHelpers/afterHouseVisitConfirmed';
import { buildRecurringEventProps, toFloatingDateTime } from '../../helpers/calendar/wallClockRecurrence';
import { scheduleCutoff, withScheduleEnd } from '../../helpers/calendar/agendaSchedulePersonas';
import ReadOnlyGuard from '../common/ReadOnlyGuard';
import { SavedIcon } from '../../StateManagment/SavedIcon';
import { Auth } from '../../StateManagment/Auth';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import taskEventTitle from '../../helpers/calendar/taskEventTitle';
import buildAdressVisitTask from '../../helpers/calendar/buildAdressVisitTask';
import useAppointmentKinds from '../../hooks/useAppointmentKinds';
import { defaultVisitTitle } from '../../helpers/tasks/resolveAppointmentKinds';
import fetchOwnAdress from '../../helpers/adressHelpers/fetchOwnAdress';
import fetchHouseholdSubtasks from '../../helpers/adressHelpers/fetchHouseholdSubtasks';
import visitDurationMinutes, { visitEndFor } from '../../helpers/tasks/visitDuration';
import CalendarSearch from './CalendarSearch.jsx';
import useTimelineView, { TIMELINE_VIEW } from '../../hooks/calendar/useTimelineView';
import TimelineModeSwitch from './TimelineModeSwitch.jsx';
import scheduleDisplayFor, { personaIdsWithSchedule, scheduleOffClasses, asScheduleBackground } from '../../helpers/calendar/scheduleDisplayFor';
import { householdPlanningEnabled } from '../../helpers/board/resolveBoardView';

/**
 * @typedef {import('../../hooks/firebase/TaskDto.jsx').Task} Task
 * @typedef {import('../../hooks/firebase/TaskDto.jsx').TaskDTO} TaskDTO
 * @typedef {'view' | 'create' | 'edit'} TaskEditorAction
 */

/**
 * @param {object} props 
 * @param {Task[]} props.docs
 * @param {string} props.userId
 * @param {(dateRange: {start: Date, end: Date}) => void} props.onDateRangeChange
 * @param {() => void} props.onStaleData
 * @param {string} [props.contextTitle]
 * @param {string} [props.contextId]
 * @param {string} [props.contextType] - 'addressVisit' | 'personaSchedule' | 'teamSchedule' | 'activityForm' | 'measureMoment'
 * @param {'dayGridMonth' | 'timeGridWeek' | 'timeGridDay' | 'listWeek' } [props.initialview]
 * @param {object} [props.headerToolbar] - Optional override for FullCalendar's headerToolbar config
 * @param {Array<{ID: string, Name?: string}>|null} [props.timelinePersonas] - Actieve medewerkers; meegeven zet de roosterweergave aan (alleen met een FullCalendar-licentie)
 * @param {Record<string, {roleId: string, roleName: string, color: string}>} [props.roleColorByPersonaId] - Rolbolletje per persona (Agenda.jsx)
 * @param {Record<string, import('../../helpers/calendar/personaAgendaColors').AgendaEventStyle>} [props.personaColorById] - Eigen agendakleur per persona; kleurt het blok zelf
 * @param {Array<object>} [props.personaAbsences] - PersonaAbsences voor de afwezig-markering
 * @param {Task[]} [props.scheduleDocs] - Ongefilterde roosterregels voor de buiten-rooster-waarschuwing
 * @param {boolean} [props.acceptAdressDrop] - Neemt een huishouden aan dat van buiten de agenda naar binnen gesleept wordt
 * @param {boolean} [props.showSearch] - Toont het zoekveld boven de agenda (volle agenda's; niet op een dashboardkaart)
 * @param {() => void} [props.onOpenHouseholdPlanning] - Zet de knop 'huishoudens' rechts in de knoppenbalk (pagina Agenda); weglaten = geen knop
 * @param {Record<string, import('../../helpers/calendar/adressStatusById').AdressStatusBadge>} [props.adressStatusById] - Status van het huishouden (woord + kleur) per AdressID; tekent een bolletje met tekst op de tweede regel van een huisbezoek
 * @param {Record<string, Date>} [props.scheduleEndByPersonaId] - Laatste roosterdag per deelnemer (uitstroomdatum); daarna toont de agenda geen roosterblok meer
 * @param {Array<string>} [props.selectedPersonaIds] - Het medewerkerfilter van CalendarFilters (leeg = iedereen); met precies één medewerker kan het rooster in week en dag een achtergrond worden
 * @returns
 */
// Stabiele lege map als standaard: een `{}` als default-parameter is élke
// render een nieuw object, en dat zou de events-memo bij elke render laten
// herrekenen voor afnemers zonder de prop (de mobiele agenda's).
// AI-CONTEXT: See docs/pitfalls.md#state-useeffect-inline-filter
const NO_ADRESS_STATUS = {};
const NO_SCHEDULE_END = {};
const NO_PERSONA_IDS = [];

const VIEW_ALIASES = {
  day: 'timeGridDay',
  list: 'listWeek',
}

export const CalendarView = ({
  docs,
  userId,
  onDateRangeChange,
  onStaleData,
  contextTitle,
  contextId,
  contextType,
  initialview = 'timeGridWeek',
  view,
  height,
  onApplyTypeFilter,
  selectedTypes,
  headerToolbar,
  timelinePersonas = null,
  roleColorByPersonaId = {},
  personaColorById = {},
  personaAbsences = [],
  scheduleDocs,
  acceptAdressDrop = false,
  showSearch = false,
  onOpenHouseholdPlanning,
  adressStatusById = NO_ADRESS_STATUS,
  scheduleEndByPersonaId = NO_SCHEDULE_END,
  selectedPersonaIds = NO_PERSONA_IDS,
}) => {

  const taskDTO = useTaskDTO();
  const [, , , setSavedMessage] = useContext(SavedIcon);
  const [auth] = useContext(Auth);
  const settings = useSettings();
  const { autoPlanHouseVisitStatus, defaultHouseVisitReminderPlan, agendaHideWeekends, agendaSnapMinutes, agendaViews, agendaScheduleDisplay } = settings;
  const kinds = useAppointmentKinds(settings);
  // Weekend, raster en zichtbare weergaven per organisatie. Eén helper, zodat de
  // knoppenbalk hier en de instellingen dezelfde lijst kennen.
  // AI-CONTEXT: See docs/features/agenda-instellingen.md
  const agendaOptions = useMemo(
    () => resolveAgendaOptions({ AgendaHideWeekends: agendaHideWeekends, AgendaSnapMinutes: agendaSnapMinutes, AgendaViews: agendaViews, AgendaScheduleDisplay: agendaScheduleDisplay }),
    [agendaHideWeekends, agendaSnapMinutes, agendaViews, agendaScheduleDisplay],
  );
  // Een expliciete `view` (uit de url) wint; een standaardweergave die de
  // organisatie verborgen heeft valt terug op de eerste zichtbare. De
  // roosterweergave en eigen werkbalken gaan ongewijzigd door.
  const resolvedInitialView = view
    ? (VIEW_ALIASES[view] || view)
    : pickInitialView(initialview, headerToolbar ? undefined : agendaOptions.visibleViews)

  // State
  /** Welke weergave nu open staat, zodat de schakelaar alleen bij het rooster verschijnt. */
  const [activeView, setActiveView] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  // Aparte stand voor een losse afspraak. De modal hierboven is die van een
  // REEKS, met drie keuzes; een losse afspraak heeft er maar één.
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  /** @type {React.RefObject<import('@fullcalendar/react').default>} */
  const calendarRef = useRef(null);

  /** @type {ReturnType<typeof useState<TaskEditorAction>>} */
  const [taskEditorAction, setTaskEditorAction] = useState(
    /** @type {TaskEditorAction} */ ('view')
  );
  
  /** @type {ReturnType<typeof useState<Date | null>>} */
  const [clickedEventDate, setClickedEventDate] = useState(null);

  /** @type {ReturnType<typeof useState<Task | null>>} */
  const [selectedTask, setSelectedTask] = useState(null);

  /** @type {ReturnType<typeof useState<Task | null>>} */
  const [followUpTask, setFollowUpTask] = useState(null);

  // De periode die FullCalendar op dit moment toont (uit datesSet). Nodig om
  // roosterblokken per occurrence te kunnen splitsen rond huisbezoeken.
  /** @type {ReturnType<typeof useState<{start: Date, end: Date} | null>>} */
  const [visibleRange, setVisibleRange] = useState(null);

  // Track external filter so we can restore it after the follow-up flow ends.
  const selectedTypesRef = useRef(selectedTypes);
  useEffect(() => { selectedTypesRef.current = selectedTypes; }, [selectedTypes]);

  const previousTypesRef = useRef(null);

  useEffect(() => {
    if (!onApplyTypeFilter) return;
    if (followUpTask && previousTypesRef.current === null) {
      previousTypesRef.current = Array.isArray(selectedTypesRef.current)
        ? selectedTypesRef.current
        : [];
      onApplyTypeFilter(['addressVisit']);
    } else if (!followUpTask && previousTypesRef.current !== null) {
      onApplyTypeFilter(previousTypesRef.current);
      previousTypesRef.current = null;
    }
  }, [followUpTask, onApplyTypeFilter]);

  // Local fallback filter — used when no external filter is wired (e.g. mobile).
  const displayDocs = useMemo(() => {
    if (!followUpTask) return docs;
    return (docs || []).filter(d => d?.Type === 'addressVisit');
  }, [docs, followUpTask]);

  // PersonaSchedule rows for the outside-rooster warning. Prefer scheduleDocs
  // (unfiltered, passed by Agenda.jsx) over docs: `docs` is the type-filtered
  // set, and unchecking the Medewerker type there would otherwise silently
  // drop all rooster data — and with it every planning-error warning.
  const personaSchedules = useMemo(
    // Na de uitstroomdatum telt een roosterregel niet meer mee (kopieën, alleen
    // voor de waarschuwing; geopend en opgeslagen worden de originelen).
    () => withScheduleEnd(
      ((scheduleDocs ?? docs) || []).filter(d => d?.Type === 'personaSchedule'),
      scheduleEndByPersonaId
    ),
    [scheduleDocs, docs, scheduleEndByPersonaId]
  );

  const docsByDocid = useMemo(
    () => new Map((displayDocs || []).map(task => [task.docid, task])),
    [displayDocs]
  );

  /**
   * De velden die elke rendering van een taak meekrijgt — ook de losse
   * stukken van een gesplitst roosterblok, zodat klik, afwezig-markering en
   * buiten-rooster-waarschuwing daar precies zo werken.
   * @param {Task} task
   */
  const extendedPropsFor = (task) => {
    // Role marker — only participant slots carry a PersonaID.
    const roleInfo =
      task.Type === 'personaSchedule'
        ? roleColorByPersonaId[task.PersonaID]
        : null;
    // Eén vergelijking voor de twee huisbezoek-velden; taskKind.guard telt ze.
    const isVisit = task.Type === 'addressVisit';

    return {
      ID: task.ID,
      TaskDocid: task.docid,
      Type: task.Type,
      PersonaID: task.PersonaID || null,
      roleColor: roleInfo?.color || null,
      roleName: roleInfo?.roleName || null,
      TeamMembers: Array.isArray(task.TeamMembers) ? task.TeamMembers : [],
      AdressName: isVisit ? (task.AdressName || '') : '',
      // Status van het huishouden op het moment van tekenen — uit de live
      // huishoudens, niet van de taak: wijzigt de status, dan wijzigt het blok.
      AdressStatus: isVisit ? (adressStatusById[task.AdressID] || null) : null,
    };
  };

  // Alle bedrading van de roosterweergave zit in één hook, die de plankiezer bij
  // het inplannen van een huisbezoek óók gebruikt.
  const timeline = useTimelineView({ personas: timelinePersonas, tasks: displayDocs || [] });

  // Rooster als blok, als achtergrond of verborgen — per weergave, alleen met
  // FIXtool (verzoek BuurtKlusBedrijf). Vóór de eerste datesSet is de weergave
  // die waarmee de agenda opent.
  // AI-CONTEXT: See docs/features/afspraak-soort-en-aanwezigheid-bkb.md
  const viewType = activeView || resolvedInitialView;
  const scheduleDisplay = scheduleDisplayFor({
    fixtool: householdPlanningEnabled(settings),
    setting: agendaOptions.scheduleDisplay,
    viewType,
    timelineMode: timeline.mode,
    selectedPersonaIds,
  });

  // Grijs buiten het rooster: alleen voor wie een roosterregel in beeld heeft.
  const idsWithSchedule = useMemo(
    () => personaIdsWithSchedule(displayDocs, scheduleEndByPersonaId),
    [displayDocs, scheduleEndByPersonaId]
  );
  const scheduleOff = scheduleOffClasses({ display: scheduleDisplay, viewType, idsWithSchedule, selectedPersonaIds });

  // Weergave-splitsing: een roosterblok wijkt voor het huisbezoek waarop
  // dezelfde persona staat. Alleen wat in beeld staat telt mee (displayDocs):
  // filtert de gebruiker de huisbezoeken weg, dan staat het rooster weer heel.
  // De roostertaak en de aanwezigheidsregistratie blijven onaangeroerd.
  const scheduleSplit = useMemo(
    () => splitScheduleAroundVisits({
      // Als achtergrond (of verborgen) valt er niets te knippen: het bezoek staat
      // er gewoon bovenop.
      schedules: scheduleDisplay === 'block' ? (displayDocs || []).filter(d => d?.Type === 'personaSchedule') : [],
      visits: (displayDocs || []).filter(d => d?.Type !== 'personaSchedule'),
      windowStart: visibleRange?.start ?? null,
      windowEnd: visibleRange?.end ?? null,
      scheduleEndByPersonaId,
    }),
    [displayDocs, visibleRange, scheduleEndByPersonaId, scheduleDisplay]
  );


  // De knop 'huishoudens' (ingang naar 'Huishoudens inplannen') staat er alleen
  // als de aanroeper hem vraagt — de pagina Agenda — én de organisatie hem niet
  // verborgen heeft. Het inplanscherm zelf en de dashboardkaart geven geen
  // handler mee en krijgen dus geen knop.
  const householdButton = typeof onOpenHouseholdPlanning === 'function' && agendaOptions.householdPlanning
  const customButtons = householdButton
    ? { [HOUSEHOLD_PLANNING_BUTTON]: { text: 'huishoudens', click: onOpenHouseholdPlanning } }
    : undefined

  // De knop 'rooster' komt er alleen bij als de weergave beschikbaar is. Een
  // aanroeper met een eigen werkbalk (dashboardkaart, mobiele agenda's) houdt
  // precies wat hij had.
  const resolvedHeaderToolbar = headerToolbar || {
    left: 'prev,next today',
    center: 'title',
    right: buildToolbarRight(agendaOptions.visibleViews, timeline.toolbarButton, householdButton),
  }

  const calendarEvents = useMemo(() => {
    const events = (displayDocs || []).flatMap(task => {
      const hidden = scheduleSplit.hiddenOccurrences[task.docid];
      // Een losse roosterregel zonder herhaling die door een bezoek wordt
      // doorsneden: alleen de stukken tonen, het geheel niet.
      if (hidden && !task.rrule) return [];

      // Een roosterregel stopt na de uitstroomdatum van zijn deelnemer: een
      // reeks krijgt een einddatum, een los blok daarna valt weg.
      const { lastDay, past } = scheduleCutoff(task, scheduleEndByPersonaId);
      if (past) return [];

      const isSchedule = task.Type === 'personaSchedule';
      if (isSchedule && scheduleDisplay === 'hidden') return [];

      const style = agendaEventStyle(task, personaColorById, getTaskTypeStyle(task.Type));

      /** @type {import('@fullcalendar/core').EventInput} */
      const event = {
        id: task.docid,
        title: taskEventTitle(task),
        start: task.Timestamp?.toDate(),
        end: task.TimestampEnd?.toDate(),
        allDay: task.TimestampEnd === undefined || task.TimestampEnd === null,

        color: style.color,
        borderColor: style.borderColor,
        backgroundColor: style.backgroundColor,
        textColor: style.textColor,
        editable: !task.rrule, // When an rrule is set, do not allow editing/dragging.

        extendedProps: extendedPropsFor(task),
      };

      // In de andere weergaven doet dit niets; FullCalendar negeert het dan.
      if (timeline.enabled) event.resourceIds = timeline.resourceIdsFor(task);

      const recurring = buildRecurringEventProps(task, lastDay);
      if (recurring) {
        event.rrule = recurring.rrule;
        event.exdate = recurring.exdate;
        event.exrule = recurring.exrule;
        event.duration = recurring.duration;
        // Alleen in de weergave: de doorsneden dagen vallen uit de reeks en
        // komen hieronder als stukken terug. Niets hiervan wordt opgeslagen.
        if (hidden) {
          event.exdate = [...(event.exdate || []), ...hidden.map(toFloatingDateTime)];
        }
      }

      // Een vlak krijgt zijn kleur uit calendar.css (wit; gearceerd bij afwezigheid).
      return [isSchedule && scheduleDisplay === 'background' ? asScheduleBackground(event) : event];
    });

    scheduleSplit.segments.forEach((segment, index) => {
      const task = docsByDocid.get(segment.docid);
      if (!task) return;
      const style = agendaEventStyle(task, personaColorById, getTaskTypeStyle(task.Type));
      events.push({
        id: `${segment.docid}#${segment.occurrenceStart.getTime()}#${index}`,
        groupId: segment.docid,
        title: taskEventTitle(task),
        start: segment.start,
        end: segment.end,
        allDay: false,
        color: style.color,
        borderColor: style.borderColor,
        backgroundColor: style.backgroundColor,
        textColor: style.textColor,
        editable: false,
        extendedProps: {
          ...extendedPropsFor(task),
          OccurrenceStart: segment.occurrenceStart,
        },
        // Een doorsneden roosterblok hoort op dezelfde rij als het blok zelf;
        // zonder rij zou het stuk in de roosterweergave verdwijnen.
        ...(timeline.enabled ? { resourceIds: timeline.resourceIdsFor(task) } : {}),
      });
    });

    return events;
    // De stand van het rooster hoort er WEL bij: zonder die twee blijft elke
    // afspraak na het omschakelen op zijn oude rij staan.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayDocs, scheduleSplit, docsByDocid, personaColorById, roleColorByPersonaId, timeline, adressStatusById, scheduleEndByPersonaId, scheduleDisplay]);

  // Context config
  const CONTEXT_CONFIG = {
    addressVisit: {
      setFields: (task, id) => ({ ...task, AdressID: id, PersonaID: null, TeamMemberID: null, ActivityID: null, MeasureMomentID: null }),
    },

    personaSchedule: {
      setFields: (task, id) => ({ ...task, PersonaID: id, AdressID: null, TeamMemberID: null, ActivityID: null, MeasureMomentID: null }),
    },

    teamSchedule: {
      setFields: (task, id) => ({ ...task, TeamMemberID: id }),
    },

    activityForm: {
      setFields: (task, id) => ({ ...task, ActivityID: id }),
    },

    measureMoment: {
      setFields: (task, id) => ({ ...task, MeasureMomentID: id }),
    },
  };

  // Handle recurring delete
  const handleRecurringDelete = async (deleteType) => {
    if (!selectedTask || !clickedEventDate) return;

    await taskDTO.deleteRecurringItem({
      task: selectedTask,
      date: clickedEventDate,
      deleteType: deleteType,
    });

    setDeleteModalOpen(false);
    setModalOpen(false);
    setSelectedTask(null);
    
    if (onStaleData) onStaleData();
  }

  /**
   * Vraagt om bevestiging voordat een afspraak verdwijnt.
   *
   * Een reeks krijgt het venster met drie keuzes (deze, deze en volgende,
   * alle); een losse afspraak het gewone bevestigingsvenster van Deccos.
   *
   * Dat laatste was een `window.confirm`: een kale browserpopup, die er anders
   * uitziet dan de rest van de app en makkelijk wordt weggeklikt zonder te
   * lezen. Updateverzoek BuurtKlusBedrijf: 'hij is meteen weg zonder dat er
   * iets gevraagd wordt'.
   *
   * @returns {void}
   */
  const initDelete = () => {
    if (!selectedTask) return;

    if (selectedTask.rrule) setDeleteModalOpen(true);
    else setConfirmDeleteOpen(true);
  }

  /**
   * Verwijdert de losse afspraak waarvoor zojuist bevestigd is.
   *
   * Zacht verwijderen (ADR-008): de afspraak blijft terug te halen uit de
   * prullenbak, en dat is wat het venster ook zegt.
   *
   * @returns {void}
   */
  const confirmDelete = () => {
    if (!selectedTask) return;

    taskDTO.remove(selectedTask.docid, auth?.ID);
    setConfirmDeleteOpen(false);
    setModalOpen(false);
    setSelectedTask(null);
  }

  const viewEvent = (id, eventDate) => {
    setSelectedTask(docs.find(doc => doc.docid === id) || null);
    setClickedEventDate(eventDate || null);
    setTaskEditorAction('view');
    setModalOpen(true);
  }

  /**
   * Inplannen vult de afspraak in (verzoek BuurtKlusBedrijf): één recept voor
   * een vervolgbezoek, een gesleept huishouden en de url-ingang. Leest het
   * huishouden eenmalig (adres, naam, toegewezen medewerkers) en de nog in te
   * plannen klussen (voor de duur). Adres en bewonersnaam via een one-shot
   * .get(), zodat de beschrijving het huisnummer niet mist als een listener
   * zijn eerste snapshot nog niet heeft.
   * AI-CONTEXT: See docs/features/afspraak-soort-en-aanwezigheid-bkb.md
   *
   * @param {object} params
   * @param {string} params.adressId
   * @param {Date} params.startDate
   * @param {Date} [params.endDate] - Leeg = voorstellen uit de klussen (anders 60 min)
   * @param {object} [params.fallback] - Waarden van de bron (zie buildAdressVisitTask)
   */
  const prepareVisit = async ({ adressId, startDate, endDate, fallback }) => {
    const [labels, adress, subtasks] = await Promise.all([
      getAdressLabels(adressId),
      fetchOwnAdress(adressId),
      endDate ? Promise.resolve(null) : fetchHouseholdSubtasks(adressId),
    ]);
    const end = endDate || visitEndFor(startDate, visitDurationMinutes(subtasks));
    const baseTask = taskDTO.newDoc({ startDate, endDate: end, userId, companyId: client, Type: 'addressVisit' });
    const adressDoc = adress ? adress.data() : {};
    const assignedTo = Array.isArray(adressDoc.AssignedTo) ? adressDoc.AssignedTo : [];
    return buildAdressVisitTask(baseTask, {
      adressId,
      labels,
      // Een huishouden zonder aparte adresvelden heeft alleen zijn titel; dan
      // is dát het adres (zelfde terugval als de takenlijst van het huishouden).
      fallback: { description: adressDoc.Title || '', ...(fallback || {}) },
      defaultTitle: defaultVisitTitle(kinds),
      teamMembers: assignedTo,
    });
  };

  const createEvent = async (startDate, endDate) => {
    if (followUpTask) {
      // Follow-up mode: pre-fill AdressID from the original task
      const taskWithContext = await prepareVisit({
        adressId: followUpTask.AdressID,
        startDate,
        endDate,
        fallback: {
          adressLabel: followUpTask.AdressLabel,
          adressName: followUpTask.AdressName,
          title: followUpTask.Title,
        },
      });
      setFollowUpTask(null);
      setSelectedTask(taskWithContext);
      setTaskEditorAction('create');
      setModalOpen(true);
      return;
    }

    // De url-ingang van één huishouden (/agenda/<id>/addressVisit): hetzelfde
    // recept, zodat ook dit pad adres, naam, titel en medewerker invult.
    if (contextId && CONTEXT_CONFIG[contextType] === CONTEXT_CONFIG.addressVisit) {
      const taskWithContext = await prepareVisit({ adressId: contextId, startDate, endDate, fallback: { title: contextTitle } });
      taskWithContext.OriginTitle = contextTitle || null;
      setSelectedTask(taskWithContext);
      setTaskEditorAction('create');
      setModalOpen(true);
      return;
    }

    const baseTask = taskDTO.newDoc({
      startDate,
      endDate,
      userId,
      companyId: client,
      Type: contextType || 'default',
    });

    const cfg = CONTEXT_CONFIG[contextType];

    const taskWithContext =
      cfg && contextId
        ? cfg.setFields(baseTask, contextId)
        : baseTask;
    taskWithContext.Type = taskWithContext.Type || contextType || "default";

    taskWithContext.OriginType = contextType || null;
    taskWithContext.OriginID = contextId || null;
    taskWithContext.OriginTitle = contextTitle || null;

    setSelectedTask(taskWithContext);
    setTaskEditorAction("create");
    setModalOpen(true);
  };

  /**
   * Een huishouden dat van buiten de agenda naar een tijdslot gesleept is.
   *
   * Opent de taakbewerker met een voorgevuld huisbezoek in plaats van het
   * bezoek meteen weg te schrijven: de duur, de medewerker en de omschrijving
   * staan nog nergens, en een halve afspraak die er al wél staat is erger dan
   * geen. De gebruiker bevestigt met 'Opslaan', net als bij elk ander bezoek.
   * AI-CONTEXT: See docs/pitfalls.md#state-nav-step-advances-without-saving
   *
   * `dataset.adressid` en niet `dataset.adressId`: de browser maakt van
   * `data-adress-id` wel `adressId`, maar van `data-adressid` juist `adressid`.
   * De lijst zet hem als `data-adressid`, dus zo komt hij er ook uit.
   *
   * @param {{draggedEl: HTMLElement, date: Date, allDay: boolean}} info - Van FullCalendar
   * @returns {Promise<void>}
   */
  const handleAdressDrop = async (info) => {
    const adressId = info?.draggedEl?.dataset?.adressid || '';
    // Iets anders dat toevallig versleepbaar is: stil laten liggen. Een lege
    // taakbewerker openen zou de gebruiker een bezoek zonder huishouden laten
    // opslaan.
    if (!adressId) return;

    const start = info.date;
    // Een dag-vak heeft geen tijd. Dan vanaf het begin van de werkdag, zodat
    // het bezoek niet om middernacht in de weergave valt. De duur komt uit de
    // nog in te plannen klussen van het huishouden (anders 60 minuten).
    const startDate = info.allDay ? new Date(start.getFullYear(), start.getMonth(), start.getDate(), 9, 0, 0) : start;

    const taskWithContext = await prepareVisit({
      adressId,
      startDate,
      fallback: { title: info?.draggedEl?.dataset?.adresslabel || '' },
    });

    setSelectedTask(taskWithContext);
    setTaskEditorAction('create');
    setModalOpen(true);
  };

  /**
   * Een afspraak uit de zoekresultaten: spring ernaartoe en open hem.
   *
   * Alleen springen is niet genoeg — dan moet de gebruiker in een volle week
   * alsnog het juiste blok zoeken. Openen zonder te springen evenmin: dan sluit
   * hij het venster en staat de agenda nog op de verkeerde maand.
   *
   * `gotoDate` vóór het openen: FullCalendar herrendert dan eerst, en het
   * venster komt over de juiste week te staan.
   *
   * @param {object} task - De gekozen afspraak
   * @returns {void}
   */
  const handleSearchSelect = (task) => {
    const datum = task?.Timestamp?.toDate?.();
    if (datum instanceof Date && !Number.isNaN(datum.getTime())) {
      calendarRef.current?.getApi?.()?.gotoDate(datum);
    }

    // De taak uit de zoeklijst komt uit een ANDERE lees dan `docs` (die draagt
    // alleen het zichtbare bereik), dus hem hier direct gebruiken en niet
    // opzoeken in docs — anders opent er niets zodra hij buiten beeld ligt.
    setSelectedTask(task);
    setClickedEventDate(datum || null);
    setTaskEditorAction('view');
    setModalOpen(true);
  };

  const handleEventUpdate = (eventInfo) => {
    taskDTO.update(eventInfo.event.id, {
      rrule: null,
      Timestamp: eventInfo.event.start 
        ? firebase.firestore.Timestamp.fromDate(eventInfo.event.start) 
        : null,
      TimestampEnd: eventInfo.event.end 
        ? firebase.firestore.Timestamp.fromDate(eventInfo.event.end) 
        : null,
    });
  }

  return (
    <ReadOnlyGuard edit="calendar.edit">
    {(canEdit) => (
    <>
      {followUpTask && (
        <div className="calendar-followup-banner">
          <span>Kies een tijdslot voor het vervolgbezoek</span>
          <button
            className="calendar-followup-banner-cancel"
            onClick={() => setFollowUpTask(null)}
          >
            Annuleren
          </button>
        </div>
      )}

      {timeline.showModeSwitch && activeView === TIMELINE_VIEW && (
        <TimelineModeSwitch mode={timeline.mode} onChange={timeline.setMode} />
      )}

      {/* Ook zichtbaar voor wie alleen mag lezen: een afspraak terugvinden is
          geen bewerking. */}
      {showSearch && <CalendarSearch onSelect={handleSearchSelect} />}

      <FullCalendar
        ref={calendarRef}
        key={resolvedInitialView}
        height={height}
        plugins={[
          rrulePlugin,
          dayGridPlugin,
          timeGridPlugin,
          listPlugin,
          interactionPlugin,
          ...timeline.plugins,
        ]}
        {...timeline.options}
        views={timeline.views}
        locale={nlLocale}
        initialView={resolvedInitialView}
        headerToolbar={resolvedHeaderToolbar}
        customButtons={customButtons}
        weekends={agendaOptions.weekends}
        snapDuration={agendaOptions.snapDuration}
        nowIndicator={true}
        // Werkdag in beeld bij openen — de standaard van FullCalendar (06:00)
        // zet de ochtenduren onnodig hoog in de weergave.
        scrollTime="08:00:00"
        events={calendarEvents}
        // Rooster als achtergrond: buiten het rooster grijs, het rooster zelf is
        // een wit vlak. Alleen de baan van wie een rooster heeft.
        resourceLaneClassNames={arg => scheduleOff.laneClassNames(arg.resource?.id)}
        viewClassNames={() => scheduleOff.viewClassNames}
        editable={canEdit}
        selectable={canEdit}
        selectMirror={true}
        dayMaxEvents={true}
        datesSet={dateInfo => {
          setActiveView(dateInfo.view?.type || null);
          setVisibleRange({ start: dateInfo.start, end: dateInfo.end });
          onDateRangeChange({
            start: dateInfo.start,
            end: dateInfo.end,
          });
        }}
        // Huisbezoek: de naam van de bewoner als tweede regel in het blok, onder
        // de titel met het adres. Elk ander blok houdt de standaardweergave
        // (`true`). Dit is de render-hook en geen eventDidMount: die vuurt maar
        // één keer, en dit moet elke render kloppen.
        eventContent={arg => {
          const { Type, AdressName, AdressStatus } = arg.event.extendedProps;
          if (Type !== 'addressVisit' || (!AdressName && !AdressStatus)) return true;
          // Maandweergave: het tweeregelige blok (tijd + titel + bewoner) is voor
          // de week- en dagweergave ontworpen. In een smal dagvakje liep het
          // door in de volgende dag en stond er boven '+2 extra' niets leesbaars
          // (verzoek BuurtKlusBedrijf 2026-09-28). Daar tekent FullCalendar zelf:
          // één regel, afgekapt; de bewoner staat in de week- en lijstweergave.
          if (arg.view.type.startsWith('dayGrid')) return true;

          const title = document.createElement('div');
          title.className = 'fc-event-title fc-sticky';
          title.textContent = arg.event.title;

          // Tweede regel: de bewoner, en daarnaast de status van het
          // huishouden als bolletje met tekst ('Anna de Vries · Ingepland').
          // De kleur van het blok zelf blijft die van de medewerker; de status
          // komt er als regel bij en verschuift niets (verzoek BuurtKlusBedrijf).
          const name = document.createElement('div');
          name.className = 'fc-event-subtitle';
          if (AdressName) name.append(document.createTextNode(AdressName));
          if (AdressStatus) {
            if (AdressName) name.append(document.createTextNode(' · '));
            const status = document.createElement('span');
            status.className = 'fc-event-status';
            const dot = document.createElement('span');
            dot.className = `status-dot status-dot--${AdressStatus.color}`;
            status.append(dot, document.createTextNode(AdressStatus.label));
            name.append(status);
          }

          if (arg.view.type.startsWith('list')) {
            return { domNodes: [title, name] };
          }

          const time = document.createElement('div');
          time.className = 'fc-event-time';
          time.textContent = arg.timeText;

          const container = document.createElement('div');
          container.className = 'fc-event-title-container';
          container.append(title, name);

          const frame = document.createElement('div');
          frame.className = 'fc-event-main-frame';
          frame.append(time, container);

          return { domNodes: [frame] };
        }}
        select={canEdit ? (e => createEvent(e.start, e.end)) : undefined}
        dateClick={canEdit ? (e => {
          if (!followUpTask) return;
          // Eén klik, geen bereik: de duur komt dan uit de klussen van het
          // huishouden (anders 60 minuten).
          createEvent(e.date, null);
        }) : undefined}
        // Een stuk van een gesplitst roosterblok draagt het docid en de start
        // van de oorspronkelijke occurrence mee; een gewoon blok heeft ze als
        // id en start.
        eventClick={e => viewEvent(
          e.event.extendedProps.TaskDocid || e.event.id,
          e.event.extendedProps.OccurrenceStart || e.event.start
        )}
        // Per rendered occurrence — a recurring series is one event object,
        // so the per-event classNames array can't mark single absence days
        // or single outside-rooster occurrences.
        eventClassNames={arg => {
          const { Type, PersonaID, TeamMembers } = arg.event.extendedProps;

          const classNames = personaAbsenceClassNames(
            {
              type: Type,
              personaId: PersonaID,
              start: arg.event.start,
              end: arg.event.end,
            },
            personaAbsences
          );

          // Rode gestippelde rand: toegewezen medewerker mét rooster, maar
          // deze occurrence valt buiten diens werktijden.
          const outsideSchedule = isTaskOutsidePersonaSchedule(
            {
              type: Type,
              teamMembers: TeamMembers,
              start: arg.event.start,
              end: arg.event.end,
            },
            personaSchedules
          );
          if (outsideSchedule) classNames.push('fc-outside-schedule');

          return classNames;
        }}
        // Alleen aan als de ouder zegt dat er een lijst naast staat. Anders
        // zou elk versleepbaar ding op de pagina in de agenda kunnen landen.
        droppable={canEdit && acceptAdressDrop}
        drop={canEdit && acceptAdressDrop ? handleAdressDrop : undefined}
        eventDrop={canEdit ? handleEventUpdate : undefined}
        eventResize={canEdit ? handleEventUpdate : undefined}
        eventDidMount={info => {
          const { roleColor, roleName, Type, TeamMembers } = info.event.extendedProps;

          // Rode gestippelde rand (.fc-outside-schedule) — leg uit waarom.
          // Alleen in list-view; blokweergaves krijgen een CSS-tooltip (zie
          // calendar.css), want eventDidMount vuurt niet opnieuw als de
          // roosterdata pas ná de eerste render binnenkomt. List-rijen
          // remounten wél zodra de docs-array verandert, dus daar volstaat dit.
          if (
            info.el.classList.contains('fc-list-event') &&
            isTaskOutsidePersonaSchedule(
              {
                type: Type,
                teamMembers: TeamMembers,
                start: info.event.start,
                end: info.event.end,
              },
              personaSchedules
            )
          ) {
            info.el.title = 'Valt buiten het rooster van de toegewezen medewerker';
          }

          // Een vlak heeft geen titel om een stip voor te zetten.
          if (!roleColor || info.event.display === 'background') return;
          if (info.el.querySelector('.role-color-dot')) return;

          const dot = document.createElement('span');
          dot.className = 'role-color-dot';
          dot.style.setProperty('--role-dot', roleColor);
          if (roleName) dot.title = roleName;

          const target =
            info.el.querySelector('.fc-event-title-container') ||
            info.el.querySelector('.fc-event-title') ||
            info.el.querySelector('.fc-list-event-title') ||
            info.el;
          target.prepend(dot);
        }}
      />

      <Modal
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
        pageHeader={{
          create: 'Nieuwe taak',
          edit: selectedTask?.Title || 'Taak bewerken',
          view: selectedTask?.Title || 'Taak details',
        }[taskEditorAction]}
        description={taskEditorAction === 'view' ? selectedTask?.Description : undefined}
        content={
          <>
            {taskEditorAction !== 'view' && (
              <button
                onClick={() => setTaskEditorAction('view')}
                className="calendar-modal-back-button"
                aria-label="Annuleren"
              >
                <ArrowBackOutlinedIcon />
              </button>
            )}

            <TaskEditor
              task={selectedTask}
              action={taskEditorAction}
              occurrenceDate={clickedEventDate}
              onTaskChange={async task => {
                setSelectedTask(task);
              }}
              onSave={async task => {
                await taskDTO.save(task);

                // Een NIEUW huisbezoek (vervolgbezoek, of gesleept vanuit de
                // lijst): status op 'Ingepland' en de automatische
                // herinnering(en), zoals ingesteld; een bewerking niet.
                if (taskEditorAction === 'create') {
                  const notices = await afterHouseVisitConfirmed({
                    task,
                    authId: userId,
                    autoPlan: autoPlanHouseVisitStatus,
                    reminderPlan: defaultHouseVisitReminderPlan,
                  });
                  if (notices.length > 0) setSavedMessage(notices.join('. '));
                }

                setModalOpen(false);
                onStaleData();
              }}
              onScheduleFollowUp={() => {
                setFollowUpTask(selectedTask);
                setModalOpen(false);
                setSelectedTask(null);
              }}
            />
          </>
        }
        // 'Verwijderen' blijft staan in de bewerkstand, 'Bewerken' niet.
        //
        // Wie een afspraak wil weghalen klikt vaak eerst op 'Bewerken' -- en
        // dáár verdween de knop, met als enige weg terug een pijltje zonder
        // tekst in de hoek. Dat leest als 'ik mag niet verwijderen', terwijl
        // het recht er gewoon is. Gemeld door Duurzame Vecht; nagemeten in
        // productie hadden de melder en de collega die het wél kon exact
        // hetzelfde roldocument, dus het lag niet aan de rechten.
        //
        // Bij 'create' hoort hij er niet: er is nog niets om te verwijderen.
        actions={taskEditorAction !== 'create' && canEdit ? (
          <>
            <button
              onClick={initDelete}
              className='delete-modal-delete-button'
            >
              Verwijderen
            </button>
            {taskEditorAction === 'view' && (
              <button onClick={() => setTaskEditorAction('edit')}>
                Bewerken
              </button>
            )}
          </>
        ) : null}
      />

      <DeleteModal
        deleteModal={confirmDeleteOpen}
        setDeleteModal={setConfirmDeleteOpen}
        deleteItem={confirmDelete}
        deleteName={selectedTask ? taskEventTitle(selectedTask) : 'deze afspraak'}
        softDelete
      />

      <Modal
        modalOpen={deleteModalOpen}
        setModalOpen={setDeleteModalOpen}
        pageHeader="Terugkerende taak verwijderen"
        content={
          <div className="calendar-recurring-delete-options">
            <button onClick={() => handleRecurringDelete('this')}>
              <strong>Dit evenement</strong>
              <br />
              <i>Alleen deze gebeurtenis verwijderen</i>
            </button>

            <button onClick={() => handleRecurringDelete('following')}>
              <strong>Dit en volgende evenementen</strong>
              <br />
              <i>Deze en alle toekomstige gebeurtenissen verwijderen</i>
            </button>

            <button onClick={() => handleRecurringDelete('all')}>
              <strong>Alle evenementen</strong>
              <br />
              <i>Alle gebeurtenissen in deze reeks verwijderen</i>
            </button>
          </div>
        }
        actions={
          <button
            onClick={() => setDeleteModalOpen(false)}
            className='modal-cancel-button'
          >
            Annuleren
          </button>
        }
      />
    </>
    )}
    </ReadOnlyGuard>
  );
};
